import { Check, Copy, MoreHorizontal, Plus, RefreshCw } from 'lucide-react';
import type { DomainPairDto, DomainPairDiagnosticDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sara/ui/components/dropdown-menu';
import { cn } from '@sara/ui/lib/utils';
import { useCopyToClipboard } from '../../../../hooks/useCopyToClipboard';

export const DOMAIN_CAP = 10;

interface DomainListProps {
  domains: DomainPairDto[];
  diagnosticsByPairId: Record<string, DomainPairDiagnosticDto[] | undefined>;
  pendingVerifyIds: ReadonlySet<string>;
  onVerify: (pair: DomainPairDto) => void;
  onDisable: (pair: DomainPairDto) => void;
  onDelete: (pair: DomainPairDto) => void;
  onCreate: () => void;
}

const COLUMNS = 'grid-cols-[1.6fr_0.9fr_7rem]';
const COLUMN_TEMPLATE = '1.6fr 0.9fr 7rem';

interface StatusPillProps {
  pair: DomainPairDto;
  awaitingAttach: boolean;
}

function StatusPill({ pair, awaitingAttach }: StatusPillProps) {
  const amber = { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' };

  if (pair.status === 'DISABLED') {
    return <Pill dot="bg-muted-foreground/50" text="text-muted-foreground/80" label="Disabled" />;
  }
  if (pair.status === 'ACTIVE') {
    return <Pill dot="bg-primary" text="text-primary" label="Active" />;
  }
  if (pair.status === 'MIXED') {
    return <Pill dot={amber.dot} text={amber.text} label="Inconsistent" />;
  }
  // PENDING
  return (
    <Pill dot={amber.dot} text={amber.text} label={awaitingAttach ? 'Requested' : 'Awaiting DNS'} />
  );
}

function Pill({ dot, text, label }: { dot: string; text: string; label: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[12px] font-medium', text)}>
      <span aria-hidden className={cn('h-1 w-1 rounded-full', dot)} />
      {label}
    </span>
  );
}

interface CnameChipProps {
  hostname: string;
  cnameTarget: string | null | undefined;
}

function CnameChip({ hostname, cnameTarget }: CnameChipProps) {
  const { copied, copy } = useCopyToClipboard();

  if (cnameTarget === null || cnameTarget === undefined) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-[11.5px] text-muted-foreground">{hostname}</span>
        <span className="text-[11.5px] italic text-muted-foreground/80">Requested</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[11.5px] text-muted-foreground">{hostname}</span>
      <button
        type="button"
        onClick={() => copy(cnameTarget)}
        className={cn(
          'group inline-flex w-fit items-center gap-1.5 rounded-md border border-border/60 bg-secondary/40 px-2 py-0.5',
          'font-mono text-[11.5px] text-foreground/90 transition hover:bg-secondary/70'
        )}
        aria-label={`Copy CNAME target for ${hostname}`}
      >
        <span>CNAME → {cnameTarget}</span>
        {copied ? (
          <Check className="h-3 w-3 text-primary" strokeWidth={2} />
        ) : (
          <Copy
            className="h-3 w-3 text-muted-foreground/70 group-hover:text-foreground"
            strokeWidth={1.6}
          />
        )}
      </button>
    </div>
  );
}

export function DomainList({
  domains,
  diagnosticsByPairId,
  pendingVerifyIds,
  onVerify,
  onDisable,
  onDelete,
  onCreate,
}: DomainListProps) {
  if (domains.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No custom domains yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Add your own hostname to brand the candidate-facing interview link.
        </p>
        <Button variant="tinted" shape="pill" size="sm" className="mt-6 gap-1.5" onClick={onCreate}>
          <Plus className="h-4 w-4" strokeWidth={1.6} />
          Add custom domain
        </Button>
      </div>
    );
  }

  const sorted = [...domains].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return a.interview.hostname.localeCompare(b.interview.hostname);
  });

  return (
    <DataPanel>
      <DataPanel.Header columns={COLUMN_TEMPLATE}>
        <span>Hostnames</span>
        <span>Status</span>
        <span className="sr-only">Actions</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {sorted.map((pair) => {
            const isVerifying = pendingVerifyIds.has(pair.id);
            const diagnostics = diagnosticsByPairId[pair.id];
            const isPending = pair.status === 'PENDING' || pair.status === 'MIXED';
            const isDisabled = pair.status === 'DISABLED';
            const interviewCname = pair.interview.cnameTarget ?? null;
            const apiCname = pair.api.cnameTarget ?? null;
            const awaitingAttach =
              !pair.isDefault && (interviewCname === null || apiCname === null);
            const canDelete = pair.status === 'PENDING' || pair.status === 'DISABLED';
            const showMenu = !pair.isDefault && (!isDisabled || canDelete);
            return (
              <li
                key={pair.id}
                className={cn(
                  'grid items-start gap-6 px-6 py-5',
                  COLUMNS,
                  isDisabled && 'opacity-70'
                )}
              >
                <div className="min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-[15px] font-medium tracking-tight text-foreground">
                      {pair.interview.hostname}
                    </p>
                    {pair.isDefault && (
                      <span className="rounded-md bg-secondary/80 px-1.5 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
                        Default
                      </span>
                    )}
                  </div>
                  {!pair.isDefault && (
                    <div className="space-y-1.5">
                      <CnameChip
                        hostname={pair.interview.hostname}
                        cnameTarget={pair.interview.cnameTarget}
                      />
                      <CnameChip hostname={pair.api.hostname} cnameTarget={pair.api.cnameTarget} />
                      {awaitingAttach && (
                        <p className="text-[11.5px] text-muted-foreground/80">
                          We&rsquo;ll notify you once your domain is configured and ready for DNS.
                        </p>
                      )}
                    </div>
                  )}
                  {pair.isDefault && (
                    <p className="truncate font-mono text-[12px] text-muted-foreground">
                      API: {pair.api.hostname}
                    </p>
                  )}
                  {diagnostics && diagnostics.length > 0 && (
                    <ul className="mt-2 space-y-1 text-[12px] text-muted-foreground">
                      {diagnostics.map((d) => (
                        <li key={`${pair.id}-${d.role}`} className="flex flex-col">
                          <span className="font-mono text-[11.5px]">{d.hostname}</span>
                          <span>DNS: {d.dns}</span>
                          <span>Heroku ACM: {d.acmStatus ?? 'unknown'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="pt-0.5">
                  <StatusPill pair={pair} awaitingAttach={awaitingAttach} />
                </div>
                <div className="flex shrink-0 items-center gap-2 justify-self-end pt-0.5">
                  {isPending && !pair.isDefault && (
                    <Button
                      variant="outline"
                      shape="pill"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => onVerify(pair)}
                      disabled={isVerifying || awaitingAttach}
                      title={
                        awaitingAttach
                          ? 'Verification will be available after ops attaches the domain.'
                          : undefined
                      }
                    >
                      <RefreshCw
                        className={cn('h-3.5 w-3.5', isVerifying && 'animate-spin')}
                        strokeWidth={1.6}
                      />
                      {isVerifying ? 'Verifying…' : 'Verify now'}
                    </Button>
                  )}
                  {showMenu && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full text-muted-foreground hover:text-foreground"
                          aria-label="Domain actions"
                        >
                          <MoreHorizontal className="h-4 w-4" strokeWidth={1.6} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!isDisabled && (
                          <DropdownMenuItem
                            onSelect={() => onDisable(pair)}
                            className="text-destructive focus:text-destructive"
                          >
                            Disable
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <DropdownMenuItem
                            onSelect={() => onDelete(pair)}
                            className="text-destructive focus:text-destructive"
                          >
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">{domains.length} domains</span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
